import type { DropdownProps } from '@ui/Dropdown';
import type { Placement } from 'tippy.js';

import { getPlanFeature, type PLAN_NAME } from '@readme/plans';
import Tippy from '@tippyjs/react';
import { useCallback } from 'react';

import useClassy from '@core/hooks/useClassy';
import useEnvInfo from '@core/hooks/useEnvInfo';
import Icon from '@ui/Icon';

import PlanAccessBanner from '../Banner';

import classes from './index.module.scss';

interface PlanAccessBadgeProps {
  /**
   * Type of plan access to display
   * - `addon` - User needs a plan upgrade plus an addon to access the feature. Badge will be shown.
   * - `locked` - User does not have access to the feature. Badge will be shown.
   * - `open` - User has access to the feature. Badge will NOT be shown.
   * - `trial` - User currently has trial access to the feature. Badge will NOT be shown.
   */
  access: 'addon' | 'locked' | 'open' | 'trial';
  className?: string;
  /**
   * Display only the icon
   */
  iconOnly?: boolean;
  /**
   * The visual design of the badge
   */
  kind?: 'ghost' | 'outline' | 'primary';
  placement?: Placement;
  /**
   * Indicates which plan to display in the badge.
   */
  plan: `${PLAN_NAME}`;
  /**
   * Whether to display a tooltip on hover
   */
  tooltip?: boolean;
}

/**
 * Indicates the access level of a feature based on the user's plan.
 */
export default function PlanAccessBadge({
  access,
  className,
  iconOnly = false,
  kind = 'ghost',
  placement = 'bottom',
  plan,
  tooltip = false,
}: Pick<DropdownProps, 'align' | 'justify'> & PlanAccessBadgeProps) {
  const bem = useClassy(classes, 'PlanAccessBadge');
  const planName = getPlanFeature(plan, 'name');
  const { isClient } = useEnvInfo();

  const renderBadge = useCallback(
    () => (
      <div className={bem('&', iconOnly && '_iconOnly', `_${kind}`, className)}>
        <Icon aria-hidden="true" className={bem('-icon')} name="lock" size="sm" />
        {!iconOnly && planName}
      </div>
    ),
    [bem, className, iconOnly, kind, planName],
  );

  if (access === 'open' || access === 'trial') return null;

  const badge = renderBadge();

  return tooltip ? (
    <Tippy
      appendTo={isClient ? document.body : 'parent'}
      content={
        <PlanAccessBanner className={bem('-tooltip')} kind={access === 'addon' ? 'addon' : 'paygate'} plan={plan} />
      }
      duration={150}
      interactive
      offset={[0, 5]}
      placement={placement}
      zIndex={9999999}
    >
      {badge}
    </Tippy>
  ) : (
    badge
  );
}
